import { createContext, ReactNode, useCallback, useContext, useEffect, useState } from 'react';
import { useAuthenticatedFetch } from '../hooks/useAuthenticatedFetch';
import { useTenant } from './TenantContext';
import { PLAN_REQUIRED_EVENT, PlanRequiredDetail } from '../lib/authEvents';

/** Recursos que variam por plano (mesmas chaves de backend/src/lib/planAccess.ts) */
export type PlanFeature =
  | 'interactive' | 'ai' | 'webhooks' | 'pix'
  | 'public_api' | 'integrations' | 'advanced_reports' | 'audit' | 'multi_org' | 'sequences' | 'instagram' | 'lead_ads' | 'ai_agents' | 'knowledge_base' | 'ai_audio' | 'ai_widget' | 'inbox';

export interface PlanSummary {
  plan: { name: string; displayName: string } | null;
  planStatus: string;
  trialEndsAt: string | null;
  trialDaysLeft: number | null;
  blocked: 'TRIAL_EXPIRED' | 'SUSPENDED' | 'CANCELLED' | null;
  blockedMessage: string | null;
  features: PlanFeature[];
}

interface PlanContextType {
  summary: PlanSummary | null;
  /** Já tentou carregar o plano da organização ativa (com ou sem sucesso) */
  ready: boolean;
  /** O plano da organização ativa inclui o recurso? Enquanto carrega, sim (o backend decide) */
  has: (feature: PlanFeature) => boolean;
  refresh: () => Promise<void>;
  /** Pedido de upgrade vindo de uma resposta 402 da API */
  upgrade: PlanRequiredDetail | null;
  dismissUpgrade: () => void;
}

const PlanContext = createContext<PlanContextType | undefined>(undefined);

export function PlanProvider({ children }: { children: ReactNode }) {
  const fetchAuth = useAuthenticatedFetch();
  const { selectedTenantId } = useTenant();
  const [summary, setSummary] = useState<PlanSummary | null>(null);
  const [upgrade, setUpgrade] = useState<PlanRequiredDetail | null>(null);
  const [ready, setReady] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const res = await fetchAuth('/api/auth/profile');
      if (!res.ok) return;
      const body = await res.json();
      setSummary(body?.data?.plan ?? null);
    } catch {
      /* sem resumo: o app segue e o backend continua decidindo */
    } finally {
      setReady(true);
    }
  }, [fetchAuth]);

  // Recarrega ao trocar de organização
  useEffect(() => {
    setReady(false);
    void refresh();
  }, [refresh, selectedTenantId]);

  useEffect(() => {
    const onRequired = (e: Event) => {
      setUpgrade((e as CustomEvent<PlanRequiredDetail>).detail);
      void refresh(); // o teste pode ter acabado agora
    };
    window.addEventListener(PLAN_REQUIRED_EVENT, onRequired);
    return () => window.removeEventListener(PLAN_REQUIRED_EVENT, onRequired);
  }, [refresh]);

  const has = useCallback((feature: PlanFeature) => !summary || summary.features.includes(feature), [summary]);

  return (
    <PlanContext.Provider value={{ summary, ready, has, refresh, upgrade, dismissUpgrade: () => setUpgrade(null) }}>
      {children}
    </PlanContext.Provider>
  );
}

export function usePlan(): PlanContextType {
  const ctx = useContext(PlanContext);
  if (!ctx) throw new Error('usePlan precisa estar dentro de PlanProvider');
  return ctx;
}
